import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, ViewChild, effect, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { TextareaModule } from '@openng/optimus-ui/textarea';
import { MarkdownComponent } from 'ngx-markdown';
import { finalize, Subscription, TimeoutError } from 'rxjs';
import { AssistantChatService } from '../services/assistant-chat.service';
import { IAssistantChatMessage } from '../models/assistant';
import { ButtonComponent } from '@simac/ui';

@Component({
  selector: 'app-assistant-chat-widget',
  standalone: true,
  imports: [FormsModule, TranslatePipe, TranslateDirective, TextareaModule, MarkdownComponent, ButtonComponent],
  templateUrl: './assistant-chat-widget.component.html',
  styleUrl: './assistant-chat-widget.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssistantChatWidgetComponent {
  @ViewChild('messageContainer') private messageContainer?: ElementRef<HTMLDivElement>;

  readonly isOpen = signal(false);
  readonly sending = signal(false);
  readonly messages = signal<IAssistantChatMessage[]>([]);
  message = '';
  emptyExampleMessages = this.getExampleMessages();

  private foundryConversationId: string | undefined;
  private activeRequest?: Subscription;
  private activeMessageId?: string;
  private readonly chatService = inject(AssistantChatService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    effect((onCleanup) => {
      this.messages();
      const animationFrame = requestAnimationFrame(() => {
        const container = this.messageContainer?.nativeElement;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
      onCleanup(() => cancelAnimationFrame(animationFrame));
    });
    this.destroyRef.onDestroy(() => this.activeRequest?.unsubscribe());
  }

  toggle(): void {
    this.isOpen.update((isOpen) => !isOpen);
  }

  close(): void {
    this.isOpen.set(false);
  }

  onEnter(event: Event): void {
    if (!(event instanceof KeyboardEvent) || event.shiftKey) {
      return;
    }
    event.preventDefault();
    this.sendMessage();
  }

  sendExampleMessage(message: string): void {
    this.message = message;
    this.sendMessage();
  }

  sendMessage(): void {
    const message = this.message.trim();
    if (!message || this.sending()) {
      return;
    }

    const messageId = crypto.randomUUID();
    this.activeMessageId = messageId;
    this.messages.update((messages) => [...messages, { id: crypto.randomUUID(), role: 'user', text: message }, { id: messageId, role: 'assistant', text: '', status: 'sending' }]);
    this.message = '';
    this.sending.set(true);

    this.activeRequest = this.chatService
      .sendMessage({ message, foundryConversationId: this.foundryConversationId }, messageId)
      .pipe(
        finalize(() => {
          this.sending.set(false);
          this.activeMessageId = undefined;
        }),
      )
      .subscribe({
        next: (event) => {
          switch (event.type) {
            case 'status':
              if (event.foundryConversationId) {
                this.foundryConversationId = event.foundryConversationId;
              }
              this.updateMessage(messageId, { status: event.status });
              break;
            case 'textDelta':
              this.messages.update((messages) => messages.map((item) => (item.id === messageId ? { ...item, text: item.text + event.text } : item)));
              break;
            case 'completed':
              this.foundryConversationId = event.foundryConversationId;
              this.updateMessage(messageId, { text: event.text, status: undefined });
              break;
            case 'error':
              this.updateMessage(messageId, { error: event.status, status: undefined });
              break;
          }
        },
        error: (error: unknown) =>
          this.updateMessage(messageId, {
            error: error instanceof TimeoutError ? 'timeout' : 'failed',
            status: undefined,
          }),
      });
  }

  private getExampleMessages(): string[] {
    return ['Toon een overzicht van al mijn openstaande incidenten.', 'Toon een overzicht van mijn laatste 10 incidenten.'];
  }

  stopMessage(): void {
    if (this.activeMessageId) {
      this.updateMessage(this.activeMessageId, { error: 'cancelled', status: undefined });
    }
    this.activeRequest?.unsubscribe();
  }

  private updateMessage(id: string, update: Partial<IAssistantChatMessage>): void {
    this.messages.update((messages) => messages.map((item) => (item.id === id ? { ...item, ...update } : item)));
  }
}
